



















































import Vue from 'vue'
import { defineComponent, ref, watch, computed, reactive, onMounted, watchEffect, Ref } from '@vue/composition-api'
import { parseNumber, round, generateRandomId } from '../helpers'
import { useWrapperClasses, useInputClasses, useDefaultInputFlags, useExposeFocus } from './abstractions';

export default defineComponent({
    props: {
        name: {
            type: String,
            default: ''
        },
        value: {
            type: Number,
            default: null
        },
        label: {
            type: String,
            dafault: ''
        },
        error: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        resetValue: {
            type: String,
            default: ''
        },
        showReset: {
            type: Boolean,
            default: false
        },
        showUpDown: {
            type: Boolean,
            default: false
        },
        step: {
            type: Number,
            default: 1
        },
        actionSequentialFocus: {
            type: Boolean,
            default: false
        },
        focus: {
            type: Boolean,
            default: null
        },
        decimalSeparator: {
            type: String,
            default: undefined // default is set to '.' below, keeping locale into account
        },
        decimalPlaces: {
            type: [Number, String],
            default: 0
        },
        fmt: {
            type: Function,
            default: null
        },
        locale: {
            type: Object,
            default: undefined
        },

        // WARNING: min and max are actually not applied. To be fixed.
        min:         { type: Number, default: 0},
        max:         { type: Number, default: null},
    },

    model: { prop: 'value', event: 'update:value' },

    setup(props, context) {
        const inputElement = ref() as Ref<HTMLInputElement>;

        const id = generateRandomId(props.name);

        const { isReadOnly, isDisabled } = useDefaultInputFlags(props);

        const { focused, onFocus, onBlur } = useExposeFocus(inputElement, context.emit, props);

        const decimalSeparator = computed(
            () => props.decimalSeparator ?? props.locale?.number?.decimalSeparator ?? '.');

        const decimalPlaces = computed(
            () => typeof props.decimalPlaces == 'string' ? parseInt(props.decimalPlaces) : props.decimalPlaces);

        const formattedValue = computed(() => {
            let v = props.fmt ?
                (<any>props.fmt)(props.value) :
                round(props.value, decimalSeparator.value, '', decimalPlaces.value);

            if (!focused.value) {
                return v;
            }

            if (inputElement.value) {
                let nv = parseNumber(inputElement.value.value);
                if (nv != null && nv != props.value) {
                    return v;
                }
                return inputElement.value.value;
            }

            return '';
        })

        const onInput = (evt: Event) => {
            if (evt.target instanceof HTMLInputElement) {
               context.emit('update:value', parseNumber(evt.target.value));
            }
        }

        const onReset = () => {
            context.emit('update:value', props.resetValue);
        }

        const onKeyDown = (evt: Event) => {
		    if (evt instanceof KeyboardEvent) {
                switch (evt.keyCode)
                {
                    // Down arrow
                    case 40:
                        onDown();
                        evt.preventDefault();
                        break;

                    // Up arrow
                    case 38:
                        onUp();
                        evt.preventDefault();
                        break;

                    // Enter is blocked, else it will submit the form.
                    case 13:
                        evt.preventDefault();

                    // Use default behavior for the following keys.
                    case 9:     // Tab
                    case 37:    // Left arrow
                    case 39:    // Right arrow
                    case 8:     // Backspace
                    case 46:    // Delete
                        break;

                    default:
                        if (!/[\d+-]/.test(evt.key) && evt.key != decimalSeparator.value) {
                            evt.preventDefault();
                        }
                        break;
                }
		    }
        }

        const onUp = () => {
            if (props.value != null && props.step != null) {
                context.emit('update:value', props.value + props.step );
            }
        }

        const onDown = () => {
            if (props.value != null && props.step != null) {
                context.emit('update:value', props.value - props.step );
            }
        }

        return {
            inputElement,
            id,
            wrapperClasses : useWrapperClasses(props, context.attrs),
            inputClasses : useInputClasses('number', computed(() => props.showUpDown)),
            formattedValue,
            isReadOnly,
            isDisabled,
            onInput,
            onReset,
            onUp,
            onDown,
            onKeyDown,
            onFocus,
            onBlur
        }
    }
})
